import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, AlertTriangle, XCircle, MinusCircle, RefreshCw, Database, HardDrive, Server, Plug, Clock, Users, Monitor, Headphones, Building2 } from 'lucide-react'
import { api } from '../utils/api'
import { APP_VERSION } from '../version'

type Level = 'ok' | 'warn' | 'error' | 'off'

interface StatusData {
  overall: Level
  problems: string[]
  checkedAt: string
  database: { level: Level; latencyMs: number | null; sizeBytes: number | null; tables: number | null; version: string | null; error?: string }
  counts: Record<string, number>
  storage: { uploadsBytes: number; uploadsFiles: number; uploadsWritable: boolean; databaseBytes: number | null }
  integrations: { name: string; level: Level; detail: string; companies: string[] }[]
  jobs: { name: string; level: Level; detail: string; lastRun: string | null; nextRun: string | null }[]
  system: { nodeVersion: string; platform: string; uptimeSeconds: number; memoryRssBytes: number; memoryHeapBytes: number; serverTime: string; timezone: string; env: string }
}

const LEVEL_STYLE: Record<Level, { text: string; bg: string; dot: string; label: string; Icon: typeof CheckCircle2 }> = {
  ok:    { text: 'text-green-700',  bg: 'bg-green-50 border-green-200',   dot: 'bg-green-500',  label: 'ปกติ',        Icon: CheckCircle2 },
  warn:  { text: 'text-amber-700',  bg: 'bg-amber-50 border-amber-200',   dot: 'bg-amber-500',  label: 'ควรตรวจสอบ',  Icon: AlertTriangle },
  error: { text: 'text-red-700',    bg: 'bg-red-50 border-red-200',       dot: 'bg-red-500',    label: 'ผิดปกติ',      Icon: XCircle },
  off:   { text: 'text-gray-500',   bg: 'bg-gray-50 border-gray-200',     dot: 'bg-gray-300',   label: 'ไม่ได้ใช้งาน', Icon: MinusCircle },
}

const OVERALL_TEXT: Record<Level, { title: string; sub: string }> = {
  ok:    { title: 'ระบบทำงานปกติ', sub: 'ทุกบริการทำงานได้ดี' },
  warn:  { title: 'ระบบทำงานได้ แต่มีข้อควรตรวจสอบ', sub: 'ดูรายละเอียดด้านล่าง' },
  error: { title: 'ระบบมีปัญหา', sub: 'พบบริการที่ทำงานผิดปกติ' },
  off:   { title: '', sub: '' },
}

const fmtBytes = (n: number | null) => {
  if (n == null) return '-'
  if (n < 1024) return `${n} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let v = n / 1024, i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${v.toFixed(v >= 100 ? 0 : 2)} ${units[i]}`
}

const fmtUptime = (s: number) => {
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60)
  return [d && `${d} วัน`, h && `${h} ชม.`, `${m} นาที`].filter(Boolean).join(' ')
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'medium' }) : '-'

function Badge({ level }: { level: Level }) {
  const s = LEVEL_STYLE[level]
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}/>{s.label}
    </span>
  )
}

function Card({ title, icon: Icon, children }: { title: string; icon: typeof Server; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-4"><Icon size={16} className="text-blue-500"/>{title}</h2>
      {children}
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-gray-50 last:border-0 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900 text-right break-all">{value}</span>
    </div>
  )
}

export default function SystemStatusPage() {
  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useQuery<StatusData>({
    queryKey: ['system-status'],
    queryFn: () => api.get('/status').then(r => r.data),
    refetchInterval: 30000,
    staleTime: 0,
  })

  if (isLoading) return <div className="p-8 text-gray-500">กำลังตรวจสอบสถานะระบบ...</div>
  if (error || !data) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700">
          <p className="font-semibold">ไม่สามารถดึงสถานะระบบได้</p>
          <p className="text-sm mt-1">Backend อาจไม่ตอบสนอง หรือคุณไม่มีสิทธิ์เข้าถึงหน้านี้ (ต้องเป็น Admin)</p>
          <button onClick={() => refetch()} className="mt-3 text-sm underline">ลองอีกครั้ง</button>
        </div>
      </div>
    )
  }

  const ov = LEVEL_STYLE[data.overall]
  const OvIcon = ov.Icon
  const totalBytes = (data.storage.databaseBytes ?? 0) + data.storage.uploadsBytes
  const pct = (n: number) => totalBytes ? Math.max(2, Math.round((n / totalBytes) * 100)) : 0

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">System Status</h1>
          <p className="text-sm text-gray-500">สถานะระบบ IT Management · อัปเดตอัตโนมัติทุก 30 วินาที</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">อัปเดตล่าสุด {new Date(dataUpdatedAt).toLocaleTimeString('th-TH')}</span>
          <button onClick={() => refetch()} disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''}/>รีเฟรช
          </button>
        </div>
      </div>

      {/* Overall */}
      <div className={`rounded-2xl border p-5 flex items-start gap-4 ${ov.bg}`}>
        <OvIcon size={36} className={ov.text}/>
        <div className="min-w-0">
          <p className={`text-lg font-bold ${ov.text}`}>{OVERALL_TEXT[data.overall].title}</p>
          <p className="text-sm text-gray-600">{OVERALL_TEXT[data.overall].sub}</p>
          {data.problems.length > 0 && (
            <ul className="mt-2 text-sm text-gray-700 list-disc list-inside space-y-0.5">
              {data.problems.map(p => <li key={p}>{p}</li>)}
            </ul>
          )}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Database', icon: Database, level: data.database.level, main: data.database.latencyMs != null ? `${data.database.latencyMs} ms` : 'ล้มเหลว', sub: data.database.error || `${data.database.tables ?? '-'} ตาราง` },
          { label: 'Storage (Uploads)', icon: HardDrive, level: (data.storage.uploadsWritable ? 'ok' : 'error') as Level, main: fmtBytes(data.storage.uploadsBytes), sub: `${data.storage.uploadsFiles.toLocaleString()} ไฟล์` },
          { label: 'Integrations', icon: Plug, level: (data.integrations.some(i => i.level === 'ok') ? 'ok' : 'off') as Level, main: `${data.integrations.filter(i => i.level === 'ok').length}/${data.integrations.length}`, sub: 'เปิดใช้งาน' },
          { label: 'Scheduled Jobs', icon: Clock, level: (data.jobs.some(j => j.level === 'error') ? 'error' : data.jobs.length ? 'ok' : 'off') as Level, main: `${data.jobs.length}`, sub: data.jobs.length ? 'งานที่ตั้งเวลาไว้' : 'ไม่มีงานที่ตั้งเวลา' },
        ].map(t => (
          <div key={t.label} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <t.icon size={20} className="text-blue-500"/>
              <span className={`w-2.5 h-2.5 rounded-full ${LEVEL_STYLE[t.level].dot}`}/>
            </div>
            <p className="text-xs text-gray-500">{t.label}</p>
            <p className="text-xl font-bold text-gray-900">{t.main}</p>
            <p className="text-xs text-gray-400 truncate">{t.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card title="พื้นที่จัดเก็บ" icon={HardDrive}>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Database (PostgreSQL)</span><span>{fmtBytes(data.storage.databaseBytes)}</span></div>
              <div className="h-2 rounded-full bg-gray-100"><div className="h-2 rounded-full bg-blue-500" style={{ width: `${pct(data.storage.databaseBytes ?? 0)}%` }}/></div>
            </div>
            <div>
              <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Uploads (ไฟล์แนบ/รูปภาพ)</span><span>{fmtBytes(data.storage.uploadsBytes)}</span></div>
              <div className="h-2 rounded-full bg-gray-100"><div className="h-2 rounded-full bg-purple-500" style={{ width: `${pct(data.storage.uploadsBytes)}%` }}/></div>
            </div>
            <Row label="รวมทั้งหมด" value={fmtBytes(totalBytes)}/>
            <Row label="โฟลเดอร์ uploads" value={data.storage.uploadsWritable ? 'เขียนได้' : <span className="text-red-600">เขียนไม่ได้</span>}/>
          </div>
        </Card>

        <Card title="ข้อมูลระบบ" icon={Server}>
          <Row label="Version" value={<span className="text-blue-600">{APP_VERSION}</span>}/>
          <Row label="Node.js" value={data.system.nodeVersion}/>
          <Row label="PostgreSQL" value={data.database.version || '-'}/>
          <Row label="Platform" value={data.system.platform}/>
          <Row label="Uptime" value={fmtUptime(data.system.uptimeSeconds)}/>
          <Row label="Memory (RSS)" value={fmtBytes(data.system.memoryRssBytes)}/>
          <Row label="Server Time" value={fmtDate(data.system.serverTime)}/>
          <Row label="Timezone" value={data.system.timezone}/>
        </Card>
      </div>

      {/* Data counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'บริษัท', v: data.counts.companies, icon: Building2 },
          { label: 'ผู้ใช้งาน (Active)', v: data.counts.activeUsers, sub: `จากทั้งหมด ${data.counts.users ?? '-'}`, icon: Users },
          { label: 'Assets', v: data.counts.assets, icon: Monitor },
          { label: 'Tickets ที่เปิดอยู่', v: data.counts.openTickets, sub: `จากทั้งหมด ${data.counts.tickets ?? '-'}`, icon: Headphones },
        ].map(c => (
          <div key={c.label} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><c.icon size={18}/></div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500">{c.label}</p>
              <p className="text-lg font-bold text-gray-900">{c.v?.toLocaleString() ?? '-'}</p>
              {c.sub && <p className="text-xs text-gray-400">{c.sub}</p>}
            </div>
          </div>
        ))}
      </div>

      <Card title="Integrations" icon={Plug}>
        <div className="divide-y divide-gray-50">
          {data.integrations.map(i => (
            <div key={i.name} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{i.name}</p>
                <p className="text-xs text-gray-400 truncate">{i.detail}{i.companies.length > 0 && ` · ${i.companies.join(', ')}`}</p>
              </div>
              <Badge level={i.level}/>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Scheduled Jobs" icon={Clock}>
        {data.jobs.length === 0 ? (
          <p className="text-sm text-gray-400">ไม่มีงานที่ตั้งเวลาไว้ (User Sync / OneDrive Backup)</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {data.jobs.map(j => (
              <div key={j.name} className="py-2.5 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{j.name}</p>
                  <p className="text-xs text-gray-500 break-words">{j.detail}</p>
                  <p className="text-xs text-gray-400 mt-0.5">รันล่าสุด {fmtDate(j.lastRun)} · รอบถัดไป {fmtDate(j.nextRun)}</p>
                </div>
                <Badge level={j.level}/>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}
